import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { notificationsApi } from '@/lib/api/notifications.api';
import { queryKeys } from '../query-keys';
import { toast } from 'sonner';
import type {
  UpdateNotificationPreferenceDto,
  BulkUpdateNotificationPreferencesDto,
} from '@scrambl/shared';
import { createLogger } from '@/lib/logger';

const logger = createLogger('hooks/mutations/useNotificationsMutations');

interface NotificationListCache {
  data: Array<{ id: string; status: string; type?: string }>;
  total: number;
}

function getCachedUnreadNotifications(queryClient: QueryClient) {
  const unread = new Map<string, { type?: string }>();
  const cachedLists = queryClient.getQueriesData<NotificationListCache>({
    queryKey: queryKeys.notifications.lists(),
  });

  for (const [, list] of cachedLists) {
    for (const notification of list?.data ?? []) {
      if (notification.status === 'UNREAD') {
        unread.set(notification.id, { type: notification.type });
      }
    }
  }

  return unread;
}

function decrementUnreadCount(queryClient: QueryClient, amount: number) {
  if (amount <= 0) return;
  queryClient.setQueryData<{ count: number }>(
    queryKeys.notifications.unreadCount(),
    (old) => ({ count: Math.max(0, (old?.count ?? 0) - amount) }),
  );
}

/**
 * Mutation hook for marking a notification as read
 */
export function useMarkNotificationAsReadMutation() {
  const queryClient = useQueryClient();
return useMutation({
    mutationFn: async (notificationId: string) => {
      return notificationsApi.markAsRead(notificationId);
    },
    onMutate: async (notificationId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.all });

      // Optimistically update the notification
      queryClient.setQueriesData<{ data: any[]; unreadCount?: number }>(
        { queryKey: queryKeys.notifications.lists() },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((n) =>
              n.id === notificationId
                ? { ...n, status: 'READ' as const, readAt: new Date() }
                : n
            ),
          };
        }
      );

      // Optimistically update unread count
      queryClient.setQueryData<{ count: number }>(
        queryKeys.notifications.unreadCount(),
        (old) => {
          if (!old) return old;
          return { count: Math.max(0, old.count - 1) };
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount() });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.feed() });
    },
    onError: (error) => {
      logger.error('[useMarkNotificationAsReadMutation] Error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to mark notification as read');
      // Revert optimistic update
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}

/**
 * Mutation hook for marking multiple notifications as read
 */
export function useMarkMultipleNotificationsAsReadMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationIds: string[]) => {
      return notificationsApi.markMultipleAsRead(notificationIds);
    },
    onMutate: async (notificationIds) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.all });
      const cachedUnread = getCachedUnreadNotifications(queryClient);
      const unreadCount = notificationIds.filter((id) =>
        cachedUnread.has(id),
      ).length;

      // Optimistically update notifications
      queryClient.setQueriesData<{ data: any[] }>(
        { queryKey: queryKeys.notifications.lists() },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((n) =>
              notificationIds.includes(n.id)
                ? { ...n, status: 'READ' as const, readAt: new Date() }
                : n
            ),
          };
        }
      );

      // Optimistically update unread count
      queryClient.setQueryData<{ count: number }>(
        queryKeys.notifications.unreadCount(),
        (old) => {
          if (!old) return old;
          return { count: Math.max(0, old.count - unreadCount) };
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount() });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.feed() });
      toast.success('Notifications marked as read');
    },
    onError: (error) => {
      logger.error('[useMarkMultipleNotificationsAsReadMutation] Error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to mark notifications as read');
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}

/**
 * Mutation hook for marking all notifications as read
 */
export function useMarkAllNotificationsAsReadMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return notificationsApi.markAllAsRead();
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.all });

      // Optimistically update all notifications
      queryClient.setQueriesData<{ data: any[] }>(
        { queryKey: queryKeys.notifications.lists() },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((n) => ({
              ...n,
              status: 'READ' as const,
              readAt: n.status === 'UNREAD' ? new Date() : n.readAt,
            })),
          };
        }
      );

      // Optimistically set unread count to 0
      queryClient.setQueryData<{ count: number }>(
        queryKeys.notifications.unreadCount(),
        () => ({ count: 0 })
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount() });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.feed() });
      toast.success('All notifications marked as read');
    },
    onError: (error) => {
      logger.error('[useMarkAllNotificationsAsReadMutation] Error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to mark all notifications as read');
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}

/**
 * Mutation hook for dismissing a notification
 */
export function useDismissNotificationMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      return notificationsApi.dismissNotification(notificationId);
    },
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.all });
      const wasUnread = getCachedUnreadNotifications(queryClient).has(notificationId);

      // Optimistically remove the notification
      queryClient.setQueriesData<{ data: any[]; total: number }>(
        { queryKey: queryKeys.notifications.lists() },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.filter((n) => n.id !== notificationId),
            total: old.total - 1,
          };
        }
      );

      // Optimistically update unread count if needed
      if (wasUnread) decrementUnreadCount(queryClient, 1);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount() });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.feed() });
      toast.success('Notification dismissed');
    },
    onError: (error) => {
      logger.error('[useDismissNotificationMutation] Error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to dismiss notification');
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}

/**
 * Mutation hook for dismissing all notifications, optionally scoped to types
 * (the feed's per-lane "Dismiss all")
 */
export function useDismissAllNotificationsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (types?: string[]) => {
      return notificationsApi.dismissAll(types);
    },
    onMutate: async (types) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.all });

      const typeSet = types?.length ? new Set(types) : null;
      const cachedUnread = getCachedUnreadNotifications(queryClient);
      const removedUnread = typeSet
        ? [...cachedUnread.values()].filter(
            (notification) =>
              notification.type && typeSet.has(notification.type),
          ).length
        : cachedUnread.size;

      queryClient.setQueriesData<NotificationListCache>(
        { queryKey: queryKeys.notifications.lists() },
        (old) => {
          if (!old) return old;
          const data = typeSet
            ? old.data.filter(
                (notification) =>
                  !notification.type || !typeSet.has(notification.type),
              )
            : [];
          return {
            ...old,
            data,
            total: Math.max(0, old.total - (old.data.length - data.length)),
          };
        },
      );

      if (typeSet) {
        decrementUnreadCount(queryClient, removedUnread);
      } else {
        queryClient.setQueryData(queryKeys.notifications.unreadCount(), {
          count: 0,
        });
      }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount() });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.feed() });
      toast.success(
        `${result.dismissed} notification${result.dismissed === 1 ? '' : 's'} dismissed`,
      );
    },
    onError: (error) => {
      logger.error('[useDismissAllNotificationsMutation] Error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to dismiss notifications');
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}

/**
 * Mutation hook for dismissing multiple notifications
 */
export function useDismissMultipleNotificationsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationIds: string[]) => {
      return notificationsApi.dismissMultiple(notificationIds);
    },
    onMutate: async (notificationIds) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.all });
      const cachedUnread = getCachedUnreadNotifications(queryClient);
      const unreadCount = notificationIds.filter((id) =>
        cachedUnread.has(id),
      ).length;

      // Optimistically remove notifications
      queryClient.setQueriesData<{ data: any[]; total: number }>(
        { queryKey: queryKeys.notifications.lists() },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.filter((n) => !notificationIds.includes(n.id)),
            total: old.total - notificationIds.length,
          };
        }
      );

      // Optimistically update unread count
      decrementUnreadCount(queryClient, unreadCount);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount() });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.feed() });
      toast.success('Notifications dismissed');
    },
    onError: (error) => {
      logger.error('[useDismissMultipleNotificationsMutation] Error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to dismiss notifications');
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}

/**
 * Mutation hook for updating a notification preference
 */
export function useUpdateNotificationPreferenceMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (preference: UpdateNotificationPreferenceDto) => {
      return notificationsApi.updatePreference(preference);
    },
    onMutate: async (preference) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.preferences() });

      // Optimistically update preference
      queryClient.setQueryData<any[]>(
        queryKeys.notifications.preferences(),
        (old) => {
          if (!old) return old;
          return old.map((p) =>
            p.channel === preference.channel && p.type === preference.type
              ? { ...p, ...preference }
              : p
          );
        }
      );
    },
    onSuccess: (updatedPreference) => {
      queryClient.setQueryData<any[]>(
        queryKeys.notifications.preferences(),
        (old) => {
          if (!old) return old;
          return old.map((p) =>
            p.channel === updatedPreference.channel && p.type === updatedPreference.type
              ? updatedPreference
              : p
          );
        }
      );
      toast.success('Notification preference updated');
    },
    onError: (error) => {
      logger.error('[useUpdateNotificationPreferenceMutation] Error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update preference');
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.preferences() });
    },
  });
}

/**
 * Mutation hook for updating multiple notification preferences
 */
export function useUpdateMultipleNotificationPreferencesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BulkUpdateNotificationPreferencesDto) => {
      return notificationsApi.updateMultiplePreferences(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.preferences() });
      toast.success('Notification preferences updated');
    },
    onError: (error) => {
      logger.error('[useUpdateMultipleNotificationPreferencesMutation] Error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update preferences');
    },
  });
}

/**
 * Mutation hook for setting default notification preferences
 */
export function useSetDefaultNotificationPreferencesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return notificationsApi.setDefaultPreferences();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.preferences() });
      toast.success('Default preferences restored');
    },
    onError: (error) => {
      logger.error('[useSetDefaultNotificationPreferencesMutation] Error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to set default preferences');
    },
  });
}
